import { Middleware } from "@reduxjs/toolkit";
import { api } from "../../api/api";
import { IInternalLogEntry, ILogEntry, LogExecuteOn } from "./accessLogs.types";

const isValidLogData = (logData: any): logData is ILogEntry => {
  return (
    logData &&
    typeof logData.module_name === "string" && logData.module_name.trim() !== "" &&
    typeof logData.sub_module_name === "string" && logData.sub_module_name.trim() !== "" &&
    typeof logData.action_name === "string" && logData.action_name.trim() !== ""
  );
};

/**
 * Extracts the lifecycle suffix from a createAsyncThunk action type.
 * e.g. "leads/upsertLead/fulfilled" → "fulfilled"
 */
const getThunkLifecycle = (actionType: string): LogExecuteOn | null => {
  if (actionType.endsWith("/pending")) return "pending";
  if (actionType.endsWith("/fulfilled")) return "fulfilled";
  if (actionType.endsWith("/rejected")) return "rejected";
  return null;
};

/**
 * Strips the internal `executeOn` property before sending to the server.
 */
const stripInternalProps = (logData: ILogEntry | IInternalLogEntry): ILogEntry => {
  const { executeOn, ...rest } = logData as IInternalLogEntry;
  return rest;
};

export const accessLogMiddleware: Middleware = (_store) => (next) => (action: any) => {
  // logData can come from:
  // 1. Plain action: action.logData (dispatched manually inside thunks, e.g. { type: "custom/log", logData })
  // 2. createAsyncThunk lifecycle actions: action.meta.arg.logData
  const logData: ILogEntry | IInternalLogEntry | undefined =
    action?.logData ?? action?.meta?.arg?.logData;

  if (logData && isValidLogData(logData)) {
    const internalLogData = logData as IInternalLogEntry;
    const hasExecuteOn = Array.isArray(internalLogData.executeOn) && internalLogData.executeOn.length > 0;

    let shouldLog = false;

    if (hasExecuteOn) {
      // For createAsyncThunk actions, only log on matching lifecycle
      const lifecycle = getThunkLifecycle(action.type);
      if (lifecycle && internalLogData.executeOn.includes(lifecycle)) {
        shouldLog = true;
      }
      // For plain actions with executeOn, log if action type is "custom/log"
      if (!lifecycle && action.type === "custom/log") {
        shouldLog = true;
      }
    } else {
      // No executeOn means log immediately (plain action / manual dispatch)
      shouldLog = true;
    }

    if (shouldLog) {
      const payload = stripInternalProps(logData);
      api.post("/log-access", payload).catch(() => {});
    }
  }

  return next(action);
};
